import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { ALL_STORES, prettifyStoreName, normalizeWorkshopSlug } from "./workshop/workshopCapacityUtils";

const ROYKEN_SITE_URL = (process.env.REACT_APP_ROYKEN_SITE_URL || "https://roykensykkelservice.no").replace(/\/+$/, "");
const KAT_LABELS = { sykkelservice: "Sykkel", skiservice: "Ski" };
const fmtKr = (n) => (typeof n === "number" ? `${n.toLocaleString("nb-NO")} kr` : "");

// Delt "Godkjenn og book tid"-panel for akutthenvendelser og "Spør mekaniker"-
// chat — mekanikers behandling og bookingopprettelse er identisk for begge,
// forskjellen er kun hvilket endepunkt som kalles og hva som ev. er
// forhåndsvalgt av kunden.
export default function GodkjennOgBookPanel({
  endpoint,
  initialKategori = "",
  initialTjenesteSlug = "",
  initialTjenester = [],
  initialButikkSlug = "",
  onBooked,
}) {
  const [alleTjenester, setAlleTjenester] = useState([]);
  const [kategori, setKategori] = useState("");
  const [tjenesteSlug, setTjenesteSlug] = useState("");
  const [valgteServiceSlugs, setValgteServiceSlugs] = useState([]);
  const [butikkSlug, setButikkSlug] = useState(ALL_STORES[0]);
  const [leveringsDato, setLeveringsDato] = useState("");
  const [forventetFerdigDato, setForventetFerdigDato] = useState("");
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const initialisert = useRef(false);

  useEffect(() => {
    fetch(`${ROYKEN_SITE_URL}/tjenester.json`)
      .then((r) => (r.ok ? r.json() : { tjenester: [] }))
      .then((d) => setAlleTjenester(Array.isArray(d.tjenester) ? d.tjenester : []))
      .catch(() => setAlleTjenester([]));
  }, []);

  const tjenestetyperForKategori = alleTjenester.filter((t) => t.kategori === kategori);
  const valgtTjenestetype = tjenestetyperForKategori.find((t) => t.slug === tjenesteSlug);
  const tilgjengeligeServices = valgtTjenestetype?.service || [];

  // Forhåndsutfyll fra kundens skjema (butikk/kategori/tjenestetype/valgte
  // tjenester), kun én gang, når katalogen er lastet.
  useEffect(() => {
    if (initialisert.current || !alleTjenester.length) return;

    const normalisertButikk = normalizeWorkshopSlug(initialButikkSlug || "");
    if (normalisertButikk && ALL_STORES.includes(normalisertButikk)) {
      setButikkSlug(normalisertButikk);
    }
    if (initialKategori) setKategori(initialKategori);
    if (initialTjenesteSlug) setTjenesteSlug(initialTjenesteSlug);

    if (initialTjenesteSlug && initialTjenester?.length) {
      const tType = alleTjenester.find((t) => t.slug === initialTjenesteSlug);
      const services = tType?.service || [];
      const slugs = initialTjenester.map((t) => t.slug).filter(Boolean);
      const matched = services.filter((s) => slugs.includes(s.slug)).map((s) => s.slug);
      if (matched.length) setValgteServiceSlugs(matched);
    }
    initialisert.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alleTjenester]);

  const toggleService = (slug) => {
    setValgteServiceSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const handleGodkjennBook = async () => {
    setBookingError("");
    if (!kategori || !tjenesteSlug) {
      setBookingError("Velg kategori og tjenestetype.");
      return;
    }
    if (valgteServiceSlugs.length === 0) {
      setBookingError("Velg minst én tjeneste.");
      return;
    }
    if (!leveringsDato) {
      setBookingError("Velg en dato.");
      return;
    }
    const tjenester = tilgjengeligeServices
      .filter((s) => valgteServiceSlugs.includes(s.slug))
      .map((s) => ({ name: s.name, price: s.price, duration: s.duration, slug: s.slug, type: s.type }));

    setBooking(true);
    try {
      const r = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          butikkNavn: prettifyStoreName(butikkSlug),
          butikkSlug,
          verkstedSlug: butikkSlug,
          leveringsDato: leveringsDato || null,
          forventetFerdigDato: forventetFerdigDato || null,
          kategori,
          tjenesteSlug,
          tjenesteType: valgtTjenestetype?.tjenestetype || "",
          tjenester,
          ansatt: { name: Cookies.get("selectedEmployee") || "" },
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Kunne ikke godkjenne og booke");
      onBooked?.(data);
    } catch (e) {
      setBookingError(e.message);
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="rounded-xl border border-green-200 bg-green-50/40 p-4 space-y-3">
      <p className="text-sm font-semibold text-gray-800">Godkjenn og book tid for kunden</p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Kategori</label>
          <select
            value={kategori}
            onChange={(e) => { setKategori(e.target.value); setTjenesteSlug(""); setValgteServiceSlugs([]); }}
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value="">Velg kategori</option>
            {Object.entries(KAT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tjenestetype</label>
          <select
            value={tjenesteSlug}
            onChange={(e) => { setTjenesteSlug(e.target.value); setValgteServiceSlugs([]); }}
            disabled={!kategori}
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400"
          >
            <option value="">{kategori ? "Velg tjenestetype" : "Velg kategori først"}</option>
            {tjenestetyperForKategori.map((t) => (
              <option key={t.slug} value={t.slug}>{t.tjenestetype}</option>
            ))}
          </select>
        </div>
      </div>

      {tjenesteSlug && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tjenester</label>
          {tilgjengeligeServices.length === 0 ? (
            <p className="text-sm text-gray-400">Ingen tjenester funnet for denne tjenestetypen.</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white">
              {tilgjengeligeServices.map((s) => (
                <label key={s.slug} className="flex items-center justify-between gap-2 text-sm px-1 py-0.5 rounded hover:bg-gray-50 cursor-pointer">
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={valgteServiceSlugs.includes(s.slug)}
                      onChange={() => toggleService(s.slug)}
                    />
                    {s.name}
                  </span>
                  {typeof s.price === "number" && <span className="text-gray-500 shrink-0">{fmtKr(s.price)}</span>}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Butikk</label>
        <select
          value={butikkSlug}
          onChange={(e) => setButikkSlug(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white"
        >
          {ALL_STORES.map((slug) => (
            <option key={slug} value={slug}>{prettifyStoreName(slug)}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Leveringsdato</label>
          <input
            type="date"
            value={leveringsDato}
            onChange={(e) => setLeveringsDato(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Ferdigdato <span className="text-gray-400 font-normal">(valgfritt)</span>
          </label>
          <input
            type="date"
            value={forventetFerdigDato}
            onChange={(e) => setForventetFerdigDato(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      {bookingError && <p className="text-xs text-red-600">{bookingError}</p>}

      <div className="flex items-center justify-end pt-1">
        <button
          onClick={handleGodkjennBook}
          disabled={booking}
          className="px-5 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
        >
          {booking ? "Booker…" : "Godkjenn og book"}
        </button>
      </div>
    </div>
  );
}
